import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, combineLatest, EMPTY, map, Observable, of, tap } from 'rxjs';
import { paginationParams } from 'src/app/components/shared/list-controller/list-controller';
import { PaginationState } from 'src/app/interfaces/pagination.interface';
import { Categories, Category, CategoryType, DefaultService } from 'src/app/swagger-api';
import { MaybeNull, Repeat } from 'src/app/utils';
import { SortCategories } from 'src/app/views/sort-elements/sort-drop-down/sort-drop-down.helpers';

function sortBy<T>(items: T[], key: keyof T, direction: SortCategories = SortCategories.NAME_ASC): T[] {
  const factor = direction === SortCategories.NAME_ASC ? 1 : -1;

  return [...items].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    return factor * String(aVal).localeCompare(String(bVal));
  });
}

@Injectable({ providedIn: 'root' })
export class CategoryService {
  private apiService = inject(DefaultService);

  public getDiseases(offset?: number, limit?: number): Observable<Categories> {
    return this.apiService
      .restCategoriesDiseasesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }

  public getSpecialties(offset?: number, limit?: number): Observable<Categories> {
    return this.apiService
      .restCategoriesSpecialtiesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }

  public getCharityFocuses(offset?: number, limit?: number): Observable<Categories> {
    return this.apiService
      .restCategoriesCharityFocusesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }

  public createDisease(title: string): Observable<Category> {
    return this.apiService.restCategoriesDiseasePost({ title }).pipe(catchError(() => EMPTY));
  }

  public createSpecialty(title: string): Observable<Category> {
    return this.apiService.restCategoriesSpecialtyPost({ title }).pipe(catchError(() => EMPTY));
  }

  public removeCategory(categoryId: string): Observable<void> {
    return this.apiService.restCategoriesCategoryDelete(categoryId).pipe(catchError(() => EMPTY));
  }

  public updateCategory(categoryId: string, categoryTitle: string, parentId?: string): Observable<Category> {
    return this.apiService
      .restCategoriesCategoryPut({ title: categoryTitle, parent_id: parentId }, categoryId)
      .pipe(catchError(() => EMPTY));
  }

  public createSubcategory(title: string, categoryId: string): Observable<Category> {
    return this.apiService
      .restCategoriesSubcategoryPost({ title, category_id: categoryId })
      .pipe(catchError(() => EMPTY));
  }
}

interface Params {
  limit: number;
  offset: number;
  keyword: string;
  sorting: SortCategories;
}

@Injectable({ providedIn: 'root' })
export abstract class CategoriesApi {
  protected apiService = inject(DefaultService);

  public abstract createCategory(title: string): Observable<Category>;
  public abstract getCategories(offset?: number, limit?: number): Observable<Categories>;

  public createSubcategory(title: string, categoryId: string): Observable<Category> {
    return this.apiService
      .restCategoriesSubcategoryPost({ title, category_id: categoryId })
      .pipe(catchError(() => EMPTY));
  }

  public updateCategory(categoryId: string, categoryTitle: string, parentId?: string): Observable<Category> {
    return this.apiService
      .restCategoriesCategoryPut({ title: categoryTitle, parent_id: parentId }, categoryId)
      .pipe(catchError(() => EMPTY));
  }

  public removeCategory(categoryId: string): Observable<void> {
    return this.apiService.restCategoriesCategoryDelete(categoryId).pipe(catchError(() => EMPTY));
  }
}

@Injectable()
export class SpecialtiesApi extends CategoriesApi {
  public createCategory(title: string): Observable<Category> {
    return this.apiService.restCategoriesSpecialtyPost({ title }).pipe(catchError(() => EMPTY));
  }

  public getCategories(offset?: number, limit?: number): Observable<Categories> {
    return this.apiService
      .restCategoriesSpecialtiesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }
}

@Injectable()
export class DiseasesApi extends CategoriesApi {
  public createCategory(title: string): Observable<Category> {
    return this.apiService.restCategoriesDiseasePost({ title }).pipe(catchError(() => EMPTY));
  }

  public getCategories(offset: number, limit: number): Observable<Categories> {
    return this.apiService
      .restCategoriesDiseasesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }
}

@Injectable()
export class CharityFocusesApi extends CategoriesApi {
  public createCategory(title: string): Observable<Category> {
    return this.apiService.restCategoriesCharityFocusPost({ title }).pipe(catchError(() => EMPTY));
  }

  public getCategories(offset: number, limit: number): Observable<Categories> {
    return this.apiService
      .restCategoriesCharityFocusesGet(offset, limit)
      .pipe(catchError(() => of({ items: [], total_count: 0 })));
  }
}

@Injectable({ providedIn: 'root' })
export class CategoriesStore {
  private api = inject(CategoriesApi);
  private destroyRef = inject(DestroyRef);

  private data$ = new BehaviorSubject<Categories>({ items: [], total_count: 0 });
  private params$ = new BehaviorSubject<Params>({
    limit: 30,
    offset: 0,
    sorting: SortCategories.NAME_ASC,
    keyword: '',
  });

  public readonly existedCategories$: Observable<Category[]> = this.data$.pipe(map(({ items }): Category[] => items));

  public readonly filteredCategories$: Observable<Categories> = combineLatest([this.params$, this.data$]).pipe(
    map(([params, data]) => {
      const { items, total_count } = data;
      const { limit, offset, sorting, keyword } = params;

      const lowerKeyword = keyword.toLowerCase();

      if (!lowerKeyword) {
        return {
          total_count,
          items: sortBy<Category>(items, 'title', sorting).slice(offset, offset + limit),
        };
      }

      const filteredByKeyword = items
        .map((parent: Category) => {
          const isParentMatch = parent.title.toLowerCase().includes(lowerKeyword);

          const matchingChildren = (parent.subcategories ?? []).filter((child) =>
            child.title.toLowerCase().includes(lowerKeyword),
          );

          if (isParentMatch || matchingChildren.length > 0) {
            return {
              ...parent,
              subcategories: keyword ? matchingChildren : parent.subcategories,
            };
          }

          return null;
        })
        .filter((category: MaybeNull<Category>): category is Category => category !== null);

      return {
        total_count: filteredByKeyword.length,
        items: sortBy<Category>(filteredByKeyword, 'title', sorting).slice(offset, offset + limit),
      };
    }),
  );

  public constructor() {
    this.refresh();
  }

  public loadCategories(pagination: PaginationState, sorting: SortCategories, keyword = ''): void {
    const [offset, limit] = paginationParams(pagination);

    this.params$.next({
      offset,
      limit,
      keyword,
      sorting,
    });
  }

  public createCategory(title: string): Observable<Category> {
    return this.api.createCategory(title);
  }

  public updateCategory(categoryId: string, categoryTitle: string, parentId?: string): Observable<Category> {
    return this.api.updateCategory(categoryId, categoryTitle, parentId);
  }

  public removeCategory(categoryId: string): Observable<void> {
    return this.api.removeCategory(categoryId);
  }

  public createSubCategory(title: string, categoryId: string): Observable<Category> {
    return this.api.createSubcategory(title, categoryId);
  }

  public refresh(): void {
    this.api
      .getCategories()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((categories: Categories) => {
          this.data$.next(categories);
        }),
      )
      .subscribe();
  }
}

@Injectable()
export class StubCategoriesRestApi extends CategoriesApi {
  public static readonly MOCK_CATEGORIES: Repeat<2, Category> = [
    {
      entity_id: 'd5',
      title: 'Influenza',
      category_type: 'DISEASES',
    },
    {
      entity_id: 'd6',
      title: 'Tuberculosis',
      category_type: 'DISEASES',
      subcategories: [
        {
          entity_id: 'd6-1',
          title: 'Pulmonary TB',
          category_type: 'DISEASES',
          parent_id: 'd6',
          parent_title: 'Tuberculosis',
        },
      ],
    },
  ];

  private readonly mockCategories$ = new BehaviorSubject<Categories>({
    items: StubCategoriesRestApi.MOCK_CATEGORIES,
    total_count: StubCategoriesRestApi.MOCK_CATEGORIES.length,
  });

  public createCategory(_title: string): Observable<Category> {
    return of(StubCategoriesRestApi.MOCK_CATEGORIES[0]);
  }

  public getCategories(_offset?: number, _limit?: number): Observable<Categories> {
    return this.mockCategories$.asObservable();
  }

  public emitMock(categories: Categories): void {
    this.mockCategories$.next(categories);
  }
}

@Injectable()
export class StubCategoryService {
  public getDiseases(_offset?: number, _limit?: number): Observable<Categories> {
    return of({
      items: this.mockCategories('DISEASES'),
      total_count: 3,
    });
  }

  public getSpecialties(_offset?: number, _limit?: number): Observable<Categories> {
    return of({
      items: this.mockCategories('SPECIALTIES'),
      total_count: 3,
    });
  }

  public createDisease(title: string): Observable<Category> {
    return of({ entity_id: 'mock-id', title, category_type: 'DISEASES' });
  }

  public createSpecialty(title: string): Observable<Category> {
    return of({ entity_id: 'mock-id', title, category_type: 'SPECIALTIES' });
  }

  public removeCategory(_categoryId: string): Observable<void> {
    return of(undefined);
  }

  public updateCategory(categoryId: string, categoryTitle: string, parentId?: string): Observable<Category> {
    return of({ entity_id: categoryId, title: categoryTitle, parent_id: parentId, category_type: 'SPECIALTIES' });
  }

  public createSubcategory(title: string, category_id: string): Observable<Category> {
    return of({ entity_id: 'mock-sub-id', title, parent_id: category_id, category_type: 'SPECIALTIES' });
  }

  private mockCategories(type: CategoryType): Category[] {
    return [
      { entity_id: '1', title: `${type} Category 1`, category_type: type },
      { entity_id: '2', title: `${type} Category 2`, category_type: type },
      {
        entity_id: '3',
        title: `${type} Category 3`,
        category_type: type,
        subcategories: [{ entity_id: '3-1', title: `${type} Subcategory 1`, category_type: type, parent_id: '3' }],
      },
    ];
  }
}
